// Язык страницы, объявленный самой страницей.
//
// ЗАЧЕМ ЭТО ОТДЕЛЬНЫЙ МЕХАНИЗМ, А НЕ ДВЕ ПРАВКИ ПО МЕСТУ.
//
// `<html lang>` ставит RootShell, и берёт он язык ИЗ МАРШРУТА: префикс /az,
// /en или параметр ?lang. Для лендингов этого хватает. Но есть страницы, чей
// язык в маршруте не выражен вовсе:
//
//   · /redeem — язык выбирает человек селектом, и до выбора страница читается
//     на языке браузера;
//   · /session/<token>/test и /result — язык лежит в строке сессии, и до её
//     загрузки его не знает даже сервер.
//
// Обе эти страницы 7 сентября печатались с lang="ru" на азербайджанском
// содержимом. На /redeem это видели только машины — переводчик и скринридер.
// На отчёте это увидела бы каждая семья: браузер по атрибуту выбирает правила
// перевода регистра, и `text-transform: uppercase` даёт «İ» только при
// lang="az". При «ru» выходит «I» без точки — «TARIX» вместо «TARİX»,
// семьдесят семь элементов на одной странице.
//
// Я сначала починил оба места по отдельности — прямой записью в
// document.documentElement.lang. Владелец справедливо это отверг: два места
// сегодня, третье завтра, и никто не вспомнит, что таких мест вообще два.
// Механизм один: страница ОБЪЯВЛЯЕТ свой язык, RootShell его слышит и ставит
// атрибут. Маршрут остаётся запасным вариантом — для всех, кто ничего не
// объявил.
//
// ПОЧЕМУ ВНЕШНЕЕ ХРАНИЛИЩЕ, А НЕ КОНТЕКСТ. RootShell стоит ВЫШЕ Outlet, то
// есть выше всех страниц. Контекст, объявленный ребёнком, до родителя не
// доходит — это направление в React запрещено. Маленькое внешнее хранилище с
// подпиской проходит вверх без нарушения правил.

import { useEffect, useSyncExternalStore } from "react";

type Слушатель = () => void;

let текущий: string | null = null;
const слушатели = new Set<Слушатель>();

function подписаться(l: Слушатель): () => void {
  слушатели.add(l);
  return () => слушатели.delete(l);
}

function снимок(): string | null {
  return текущий;
}

/**
 * На сервере объявленного языка не бывает: страница объявляет его после
 * загрузки своих данных, а SSR идёт до этого. Возвращаем null, и RootShell
 * честно берёт язык маршрута — тот же ответ, что и до появления механизма.
 */
function серверныйСнимок(): string | null {
  return null;
}

export type PageLang = "ru" | "az" | "en";

export function isPageLang(x: unknown): x is PageLang {
  return x === "ru" || x === "az" || x === "en";
}

/**
 * Язык страницы ДО того, как она что-то объявила: им печатается серверный
 * HTML. Порядок:
 *   1. `?lang=ru|az|en` у любого совпавшего маршрута;
 *   2. `pageLang` из данных загрузчика маршрута (самый глубокий выигрывает) —
 *      так /session/<token>/… получает язык сессии уже на сервере. Раньше там
 *      стоял lang="ru" на азербайджанском тесте, и Safari на iPhone предлагал
 *      «перевести с русского»: перевод переставлял слова в метку варианта,
 *      опустошал варианты и портил формулировки (19.10.2026);
 *   3. суффикс пути `_az` / `_en`, префикс `/az`, `/en`;
 *   4. `ru` — язык по умолчанию.
 */
export function routePageLanguage(
  matches: ReadonlyArray<{ search?: unknown; loaderData?: unknown }>,
  pathname: string,
): PageLang {
  for (const m of matches) {
    const s = m.search as { lang?: unknown } | undefined;
    if (s && isPageLang(s.lang)) return s.lang;
  }
  for (let i = matches.length - 1; i >= 0; i--) {
    const d = matches[i].loaderData as { pageLang?: unknown } | undefined;
    if (d && isPageLang(d.pageLang)) return d.pageLang;
  }
  if (pathname.endsWith("_az")) return "az";
  if (pathname.endsWith("_en")) return "en";
  if (pathname === "/az" || pathname.startsWith("/az/")) return "az";
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  return "ru";
}

/** Читает объявленный язык. Для RootShell. */
export function usePublishedPageLanguage(): string | null {
  return useSyncExternalStore(подписаться, снимок, серверныйСнимок);
}

/**
 * Объявляет язык страницы, пока она открыта.
 *
 * Снимает объявление при уходе — иначе следующая страница унаследовала бы
 * чужой язык и никто бы не понял почему.
 */
export function usePublishPageLanguage(lang: string | null | undefined): void {
  useEffect(() => {
    if (!lang) return;
    текущий = lang;
    слушатели.forEach((l) => l());
    return () => {
      текущий = null;
      слушатели.forEach((l) => l());
    };
  }, [lang]);
}
